import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Copy, Loader2, AlertCircle, RefreshCw, Settings } from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import ShowDetails from './ShowDetails.jsx';
import GuestReportTable from './GuestReportTable.jsx';
import QuickEditModal from './QuickEditModal.jsx';
import { sharedStyles, createButton, createGlassCard } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

function GuestReport() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();

  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pitchCopied, setPitchCopied] = useState(false);
  const [selectedGuestIndex, setSelectedGuestIndex] = useState(0);
  const [showPitch, setShowPitch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [showData, setShowData] = useState(null);
  const [userTier, setUserTier] = useState('starter'); // Default to starter
  const [isLifetime, setIsLifetime] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState(null);

  // Fetch report and show data from API
  useEffect(() => {
    const fetchReportData = async () => {
      // Redirect to sign-in if not authenticated
      if (!isLoaded) return;

      if (!isSignedIn) {
        navigate('/sign-in?redirect_url=' + encodeURIComponent(`/report/${reportId}`));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch report (with show data) and user data in parallel for maximum speed
        // Using include_show=true eliminates the need for a separate show API call
        const [reportWithShow, userData] = await Promise.all([
          makeRequest(`reports/${reportId}?include_show=true`),
          makeRequest('users/me')
        ]);

        setReportData(reportWithShow);
        setUserTier(userData.subscription_tier || 'starter');
        setIsLifetime(userData.is_lifetime || false);

        // Show data is now included in the report response
        if (reportWithShow.show) {
          setShowData(reportWithShow.show);
        }

        setLoading(false);

      } catch (err) {
        console.error('Error fetching report:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportId, isLoaded, isSignedIn, navigate, makeRequest]);

  // Show loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1rem',
        }}>
          <Loader2 size={48} style={{
            color: sharedStyles.colors.primary,
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{
            color: sharedStyles.colors.textSecondary,
            fontSize: '1.125rem',
          }}>
            Loading your guest recommendations...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '1.5rem',
          padding: '2rem',
        }}>
          <div style={{
            ...createGlassCard('default'),
            padding: '2rem',
            maxWidth: '500px',
            textAlign: 'center',
          }}>
            <AlertCircle size={48} style={{
              color: sharedStyles.colors.error,
              marginBottom: '1rem',
            }} />
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: sharedStyles.colors.text,
              marginBottom: '0.5rem',
            }}>
              Error Loading Report
            </h2>
            <p style={{
              color: sharedStyles.colors.textSecondary,
              marginBottom: '1.5rem',
            }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/')}
              style={createButton('primary')}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!reportData || !showData) {
    return (
      <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
        <Navbar />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}>
          <p style={{ color: sharedStyles.colors.textSecondary }}>
            No report data available
          </p>
        </div>
      </div>
    );
  }

  // Transform API data to match component's expected format
  const showDetails = {
    name: showData.title,
    host: showData.host,
    description: showData.description,
    idealGuest: showData.ideal_guest_profile,
    audience: showData.audience_profile,
    format: showData.format,
    topics: showData.topics_covered || []
  };

  const guestProfiles = (reportData.guests || []).map((guest, index) => ({
    id: index + 1,
    name: guest.name,
    title: guest.title,
    company: guest.company,
    relevanceScore: guest.relevance_score,
    summary: guest.summary,
    expertiseAreas: guest.expertise_areas || [],
    socialMedia: {
      linkedin: guest.linkedin || null,
      twitter: guest.x_account || null,
      website: null,
      email: guest.email || null
    },
    pitch: guest.pitch || null,
    discoveredAt: reportData.created_at  // Timestamp when this guest was found (report creation time)
  }));

  const handleCopyEmail = (email, index) => {
    navigator.clipboard.writeText(email);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleGeneratePitch = (index) => {
    setSelectedGuestIndex(index);
    setShowPitch(true);
  };

  const handleRegenerateReport = async (customCriteria = null) => {
    if (!showData) return;

    setGeneratingReport(true);

    try {
      const requestBody = {
        show_id: showData.id
      };

      // Add custom criteria if provided from modal
      if (customCriteria) {
        if (customCriteria.guestTopics) requestBody.guest_topics = customCriteria.guestTopics;
        if (customCriteria.guestExpertise) requestBody.guest_expertise = customCriteria.guestExpertise;
        if (customCriteria.guestType?.length > 0) requestBody.guest_type = customCriteria.guestType;
        if (customCriteria.guestLocation) requestBody.guest_location = customCriteria.guestLocation;
        if (customCriteria.interviewFormat) requestBody.interview_format = customCriteria.interviewFormat;
        if (customCriteria.recordingMethod) requestBody.recording_method = customCriteria.recordingMethod;
        if (customCriteria.interviewLength) requestBody.interview_length = customCriteria.interviewLength;
        if (customCriteria.timeline) requestBody.timeline = customCriteria.timeline;
        if (customCriteria.additionalRequirements) requestBody.additional_requirements = customCriteria.additionalRequirements;
      }

      const response = await makeRequest('reports', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      // Navigate to the newly generated report
      navigate(`/report/${response.report_id}`);
    } catch (error) {
      console.error('Error generating report:', error);

      if (error.message.includes('402')) {
        alert('You have reached your monthly report limit. Please upgrade your subscription or wait until next month.');
      } else if (error.message.includes('500')) {
        alert('The AI service encountered a temporary error. This usually resolves itself - please try again in a few seconds. If the problem persists, check https://status.anthropic.com/ for any ongoing issues.');
      } else {
        alert(`Failed to generate report: ${error.message}`);
      }
      setGeneratingReport(false);
    }
  };

  const handleOpenEditModal = () => {
    // Initialize with empty data (users will customize from defaults)
    const initialData = {
      guestTopics: '',
      guestExpertise: '',
      guestType: [],
      guestLocation: '',
      interviewFormat: '',
      recordingMethod: '',
      interviewLength: '',
      timeline: '',
      additionalRequirements: ''
    };

    setEditFormData(initialData);
    setShowEditModal(true);
  };

  const handleSaveAndGenerate = (formData) => {
    handleRegenerateReport(formData);
  };

  const selectedGuest = guestProfiles[selectedGuestIndex];

  // Determine if user has premium access (Pro Monthly OR Pro Lifetime)
  const hasPremiumAccess = userTier === 'pro';

  return (
    <div style={sharedStyles.pageContainer}>
      {/* Header */}
      <Navbar variant="report" />

      {/* Incomplete Report Banner */}
      {reportData && !reportData.is_completed && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '2px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '1.5rem',
          margin: '1.5rem 4rem 0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <AlertCircle size={24} style={{ color: sharedStyles.colors.error, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: sharedStyles.colors.error,
              marginBottom: '0.25rem'
            }}>
              Report Generation Failed
            </h3>
            <p style={{
              fontSize: '0.875rem',
              color: sharedStyles.colors.error,
              lineHeight: '1.5'
            }}>
              This report did not complete successfully. The AI service may have encountered an error during guest discovery.
              Please try regenerating the report using one of the buttons below.
            </p>
          </div>
        </div>
      )}

      {/* Regenerate Actions */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        padding: '1.5rem 4rem 0.5rem',
      }}>
        <button
          onClick={() => handleRegenerateReport()}
          disabled={generatingReport}
          style={{
            ...createButton('primary'),
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            padding: '0.75rem 1.25rem',
            opacity: generatingReport ? 0.6 : 1,
            cursor: generatingReport ? 'not-allowed' : 'pointer'
          }}
        >
          {generatingReport ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Generating... (30-60s)
            </>
          ) : (
            <>
              <RefreshCw size={18} />
              Regenerate with Same Criteria
            </>
          )}
        </button>

        <button
          onClick={handleOpenEditModal}
          disabled={generatingReport}
          style={{
            ...createButton('secondary'),
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            padding: '0.75rem 1.25rem',
            opacity: generatingReport ? 0.6 : 1,
            cursor: generatingReport ? 'not-allowed' : 'pointer'
          }}
        >
          <Settings size={18} />
          Customize & Regenerate
        </button>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        padding: '1rem 4rem',
        overflow: 'visible'
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'stretch',
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 'fit-content'
        }}>
          {/* Left Panel - Show Details */}
          <ShowDetails showDetails={showDetails} />

          {/* Right Panel - Guest Report */}
          <GuestReportTable
            guestProfiles={guestProfiles}
            copiedIndex={copiedIndex}
            onCopyEmail={handleCopyEmail}
            onGeneratePitch={handleGeneratePitch}
            hasPremiumAccess={hasPremiumAccess}
            showTitle={showDetails.name}
          />
        </div>
      </div>

      {/* Pitch Modal */}
      {showPitch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
          onClick={() => setShowPitch(false)}
        >
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0px 32px 64px rgba(0, 0, 0, 0.2)'
          }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${sharedStyles.colors.primary}20`,
              background: sharedStyles.colors.surfaceAlt,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Personalized Pitch for {selectedGuest.name}
                </h3>
                <p style={{
                  fontSize: '0.875rem',
                  color: sharedStyles.colors.textSecondary,
                  margin: 0
                }}>
                  AI-generated outreach message
                </p>
              </div>
              <button
                onClick={() => {
                  const emailWithSubject = `Subject: Invitation to ${showDetails.name}\n\n${selectedGuest.pitch}`;
                  navigator.clipboard.writeText(emailWithSubject);
                  setPitchCopied(true);
                  setTimeout(() => setPitchCopied(false), 2000);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: pitchCopied ? sharedStyles.colors.success : sharedStyles.colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexShrink: 0
                }}
              >
                <Copy size={16} />
                {pitchCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '1.5rem',
              overflowY: 'auto',
              maxHeight: 'calc(80vh - 140px)'
            }}>
              <div style={{
                background: sharedStyles.colors.primaryLight,
                borderRadius: '12px',
                padding: '1.25rem',
                fontSize: '0.9rem',
                lineHeight: '1.7',
                color: sharedStyles.colors.textSecondary,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
              }}>
                <p style={{ marginBottom: '1rem', fontWeight: '600' }}>
                  <strong>Subject:</strong> Invitation to {showDetails.name}
                </p>

                <div style={{
                  whiteSpace: 'pre-wrap',
                  borderTop: '1px solid rgba(234, 88, 12, 0.2)',
                  paddingTop: '1rem'
                }}>
                  {selectedGuest.pitch}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem',
              borderTop: '1px solid rgba(234, 88, 12, 0.1)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowPitch(false)}
                style={{
                  ...createButton('secondary')
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Edit Modal */}
      <QuickEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveAndGenerate}
        initialData={editFormData}
      />
    </div>
  );
}

export default GuestReport;